import { useEffect, useRef, useState } from 'react';
import { FileText } from 'lucide-react';

let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | null = null;
function carregarPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist').then(async (mod) => {
      const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
      (mod as any).GlobalWorkerOptions.workerSrc = workerUrl;
      return mod;
    });
  }
  return pdfjsPromise;
}

const cache = new Map<string, boolean>();

interface Props {
  url: string;
}

export function ThumbnailPdf({ url }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [estado, setEstado] = useState<'loading' | 'done' | 'error'>(cache.has(url) ? 'done' : 'loading');

  useEffect(() => {
    if (!url) return;
    let cancelado = false;
    // Delays de re-tentativa: cobre o caso do arquivo ainda estar sendo salvo
    // no storage quando a mensagem acabou de chegar, alem de carga lenta.
    const delays = [2000, 4000, 6000];

    async function tentarRenderizar(tentativa: number): Promise<void> {
      try {
        const pdfjs = await carregarPdfjs();
        if (cancelado) return;
        const task = (pdfjs as any).getDocument({ url, disableAutoFetch: true, disableStream: true });
        const pdf = await task.promise;
        if (cancelado) return;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1 });
        const targetWidth = 200;
        const escala = targetWidth / viewport.width;
        const escalado = page.getViewport({ scale: escala });
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = escalado.width;
        canvas.height = escalado.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        await page.render({ canvasContext: ctx, viewport: escalado }).promise;
        if (cancelado) return;
        cache.set(url, true);
        setEstado('done');
      } catch (e) {
        if (cancelado) return;
        if (tentativa < delays.length) {
          // Falha pode ser transitoria — tenta de novo com espera crescente.
          setEstado('loading');
          setTimeout(() => { if (!cancelado) void tentarRenderizar(tentativa + 1); }, delays[tentativa]);
        } else {
          console.warn('[ThumbnailPdf] falha ao renderizar apos varias tentativas', url, e);
          setEstado('error');
        }
      }
    }

    void tentarRenderizar(0);
    return () => { cancelado = true; };
  }, [url]);

  if (estado === 'error') {
    return (
      <div className="w-[200px] h-[260px] bg-red-600 rounded-lg flex flex-col items-center justify-center flex-shrink-0">
        <FileText size={40} className="text-white mb-1" />
        <span className="text-white text-xs font-bold">PDF</span>
      </div>
    );
  }
  return (
    <div className="relative w-[200px] min-h-[100px] bg-white rounded-lg overflow-hidden border border-black/10 flex-shrink-0">
      <canvas ref={canvasRef} className="block w-full h-auto" />
      {estado === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <FileText size={32} className="text-gray-300 animate-pulse" />
        </div>
      )}
    </div>
  );
}
